import { useAxios } from "@wayflyer/auth-sdk";
import type { AxiosResponse } from "axios";
import { connectByPath } from "../../data-sources";
import { useErrorModal } from "../../error-handling/app-error-modal-provider";

export type ShopifyData = {
  storeName: string;
};

export type AmazonData = {
  value: string;
};

export type WooCommerceData = {
  storeName: string;
};

export type MagentoData = {
  storeName: string;
  magentoAccessToken: string;
};

export type BigcommerceData = { storeName: string; bigcommerceAccessToken: string; otp?: string };

export type BigcommerceOtpData = { otp: string };

// should be a combination of all eventually
export type ConnectorPostData =
  | ShopifyData
  | AmazonData
  | BigcommerceData
  | BigcommerceOtpData
  | WooCommerceData
  | MagentoData;

// Every connector's typed response (each's *ConnectResponseSchema/*ConnectErrorSchema) shares this
// same flattened shape on the wire; every field is optional here because which ones are present
// depends on which connector responded and whether it errored.
type ConnectorPostResponse = {
  errorMessage?: string;
  errorField?: string;
  authUrl?: string;
};

// only for use in new style connectors that require the error strings returned
export const useConnectByPost = (): ((
  path: string,
  formData: ConnectorPostData
) => Promise<ConnectorPostResponse | undefined>) => {
  const client = useAxios();
  const { openErrorModal } = useErrorModal();
  /**
   * Init oauth by posting form data from the connection modal
   */
  const connectByPost = async (
    path: string,
    formData: ConnectorPostData
  ): Promise<ConnectorPostResponse | undefined> => {
    try {
      const response = await client.post<FormData, AxiosResponse<ConnectorPostResponse>>(path, formData);

      // error
      if (response.data.errorMessage) {
        return response.data;
      }
      // success
      if (response.data.authUrl) {
        await connectByPath(response.data.authUrl);
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        openErrorModal({
          variant: "submit-error",
          error,
        });
      }
    }
  };

  return connectByPost;
};
